import { useGetFertilizers, useGetSoilAnalyses } from '@practium/features/api';
import { useToast, type FormSelectOption } from '@practium/ui';
import { useMemo, useState } from 'react';
import { usePostAdviceCorrectionGessagem } from '../../api/hooks/recommendations';
import { GessagemSchema } from '../schemas/correction.schema';
import { useCorrectionStore } from '../store/correctionStore';
import type { CorrectionStartResponse, GessagemFormData, GessagemResult } from '../types';
import { useTabCalculation } from './useTabCalculation';

const getId = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const id = record._id ?? record.id;
		return typeof id === 'string' ? id : '';
	}
	return '';
};

const GESSAGEM_START_KEYS: Record<string, string[]> = {
	S_g: ['S_g', 'S'],
	Ca_g: ['Ca_g', 'Ca'],
	satCa_g: ['satCa_g', 'satCa'],
};

const getStartRange = (startResult: CorrectionStartResponse, param: string): string => {
	for (const key of GESSAGEM_START_KEYS[param] ?? [param]) {
		const value = startResult.faixaAdequada?.[key];
		if (value !== null && value !== undefined && value !== '') return String(value);
	}
	return '—';
};

const applyStartRangeToGessagemTable = (
	result: GessagemResult,
	startResult: CorrectionStartResponse
): GessagemResult => ({
	...result,
	table: result.table.map((row) => ({
		...row,
		range: getStartRange(startResult, row.param),
	})),
});

const retainCurrentAndRange = (
	next: GessagemResult,
	previous: GessagemResult | null
): GessagemResult => ({
	...next,
	table: next.table.map((row) => {
		const oldRow = previous?.table.find((candidate) => candidate.param === row.param);
		return oldRow ? { ...row, current: oldRow.current, range: oldRow.range } : row;
	}),
});

export const useGessagemTab = () => {
	const toast = useToast();
	const {
		advice,
		correctionStart,
		gessagem,
		gessagemAjuste,
		gessagemResult,
		gessagemHasCalculated,
		setGessagemField,
		setGessagemAjusteField,
		setGessagemResult,
		setGessagemHasCalculated,
		resetGessagem,
	} = useCorrectionStore();

	const areaId = getId(advice?.area);
	const { data: fertilizers = [], isLoading: isLoadingFertilizers } = useGetFertilizers();
	const { data: soilAnalyses = [], isLoading: isLoadingSoilAnalyses } = useGetSoilAnalyses({
		areaId: areaId || undefined,
		enabled: !!areaId,
	});

	const conditionerOptions = useMemo<FormSelectOption[]>(
		() =>
			fertilizers
				.filter((fertilizer) => fertilizer.category === 'conditioner')
				.map((fertilizer) => ({
					value: fertilizer._id,
					label: `${fertilizer.name}${fertilizer.Ca != null ? ` (Ca: ${fertilizer.Ca}${fertilizer.S != null ? `, S: ${fertilizer.S}` : ''})` : ''}`,
				})),
		[fertilizers]
	);

	const soilAnalysisOptions = useMemo<FormSelectOption[]>(
		() =>
			soilAnalyses
				.filter((analysis) => !areaId || getId(analysis.area) === areaId)
				.map((analysis) => ({
					value: analysis._id,
					label: analysis.name || analysis.sampledAt || analysis._id,
				})),
		[areaId, soilAnalyses]
	);

	const { mutateAsync: calcularGessagem } = usePostAdviceCorrectionGessagem();
	const [isAdjusting, setIsAdjusting] = useState(false);

	const calculateResult = async (form: GessagemFormData) => {
		const adviceId = advice?._id;
		if (!adviceId) throw new Error('Recomendação não encontrada para calcular gessagem.');

		const fixedDepth = form.forma === 'total' || form.forma === 'faixa';
		const result = await calcularGessagem({
			adviceId,
			soilAnalysisId: form.soilAnalysis,
			productId: form.condicionador,
			satCa2: Number(form.satCa2),
			forma: form.forma,
			formaConfig: {
				p: fixedDepth ? 0.2 : Number(form.profundidade),
				l: Number(form.largura || 0),
				c: Number(form.comprimento || 0),
			},
		});
		setGessagemField('interpretacao', result.interpretacao);
		return correctionStart ? applyStartRangeToGessagemTable(result, correctionStart) : result;
	};

	const tabCalculation = useTabCalculation<GessagemFormData, GessagemResult>({
		form: gessagem,
		ajuste: gessagemAjuste,
		result: gessagemResult,
		hasCalculated: gessagemHasCalculated,
		setField: setGessagemField,
		setAjusteField: setGessagemAjusteField,
		setResult: setGessagemResult,
		setHasCalculated: setGessagemHasCalculated,
		reset: resetGessagem,
		schema: GessagemSchema,
		calculate: calculateResult,
		tabName: 'Gessagem',
		onSetField: (key) => {
			if (key === 'soilAnalysis') setGessagemField('interpretacao', '');
			if (key !== 'interpretacao') {
				setGessagemResult(null);
				setGessagemHasCalculated(false);
			}
		},
	});

	const handleAdjustDose = async () => {
		const dose = Number(gessagemAjuste.dose);
		if (!gessagem.condicionador || !Number.isFinite(dose) || dose < 0) {
			toast.error('Informe uma dose válida para recalcular a gessagem.');
			return;
		}

		setIsAdjusting(true);
		try {
			const result = await calculateResult(gessagem);
			const mappedResult = retainCurrentAndRange(result, gessagemResult);
			setGessagemResult(mappedResult);
			setGessagemAjusteField('dose', mappedResult.ajuste.dose);
			setGessagemAjusteField('doseSecundaria', mappedResult.ajuste.doseSecundaria);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao recalcular gessagem.');
		} finally {
			setIsAdjusting(false);
		}
	};

	return {
		...tabCalculation,
		conditionerOptions,
		soilAnalysisOptions,
		isLoadingOptions: isLoadingFertilizers || isLoadingSoilAnalyses,
		handleAdjustDose,
		isAdjusting,
	};
};
